import React, { useState } from "react";
import Card from "../../../components/base/Card";
import Button from "../../../components/base/Button";
import { API_ENDPOINTS } from "../../../config/api";

const ContactFormSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    contactMethod: "email",
    phone: "",
    service: "",
    deadline: "",
    details: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch(
        API_ENDPOINTS.CONTACT_FORM,
        {
          method: "POST",
          body: formDataToSubmit,
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          organization: "",
          contactMethod: "email",
          phone: "",
          service: "",
          deadline: "",
          details: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-white">
      <div className="max-w-4xl mx-auto">
        <Card className="p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Name + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#e18033] outline-none"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#e18033] outline-none"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Organization */}
            <div>
              <label
                htmlFor="organization"
                className="block text-sm font-medium mb-2"
              >
                Organization
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#e18033] outline-none"
                placeholder="Enter your organization"
              />
            </div>

            {/* Preferred Contact Method */}
            <div>
              <p className="block text-sm font-medium mb-2">
                Preferred Contact Method *
              </p>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="contactMethod"
                    value="email"
                    checked={formData.contactMethod === "email"}
                    onChange={handleInputChange}
                  />
                  Email
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="contactMethod"
                    value="phone"
                    checked={formData.contactMethod === "phone"}
                    onChange={handleInputChange}
                  />
                  Phone
                </label>
              </div>
            </div>

            {/* Phone Field (conditional required) */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium mb-2"
              >
                Phone Number {formData.contactMethod === "phone" ? "*" : ""}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required={formData.contactMethod === "phone"}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#e18033] outline-none"
                placeholder="Enter your phone number"
              />
            </div>

            {/* Design Service Type */}
            <div>
              <label
                htmlFor="service"
                className="block text-sm font-medium mb-2"
              >
                Drafting Service Type *
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#e18033] outline-none"
              >
                <option value="">Select a service</option>
                <option value="autocad">AutoCAD Drafting</option>
                <option value="revit">Revit Modeling</option>
                <option value="dialux">Dialux Lighting</option>
                <option value="architectural">Architectural Drawings</option>
                <option value="electrical">Electrical Plans</option>
                <option value="structural">Structural Drawings</option>
                <option value="consultation">Take Off/ Low Proposal</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Deadline */}
            <div>
              <label
                htmlFor="deadline"
                className="block text-sm font-medium mb-2"
              >
                When do you need the drafting by? *
              </label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                value={formData.deadline}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#e18033] outline-none"
              />
            </div>

            {/* Project Details */}
            <div>
              <label
                htmlFor="details"
                className="block text-sm font-medium mb-2"
              >
                Project Details (Optional)
              </label>
              <textarea
                id="details"
                name="details"
                value={formData.details}
                onChange={handleInputChange}
                rows={6}
                maxLength={1000}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#e18033] outline-none resize-none"
                placeholder="Please describe your project requirements, timeline, and any specific details..."
              />
              <p className="text-sm text-gray-500 mt-2">
                Maximum 1000 characters
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
              <div className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-send-plane-line"></i>
              </div>
            </Button>

            {/* Status Messages */}
            {submitStatus === "success" && (
              <p className="text-green-600 mt-2">
                ✅ Message sent successfully!
              </p>
            )}
            {submitStatus === "error" && (
              <p className="text-red-600 mt-2">
                ❌ Something went wrong. Please try again.
              </p>
            )}
          </form>
        </Card>
      </div>
    </section>
  );
};

export default ContactFormSection;
